import { useEffect } from 'react';

import { throttle } from 'lodash';

import { MESSAGE_TYPES } from '@/lib/constants/widget-events';
import { checkSession, sessionChangeKey } from '@/lib/guest-auth/browser-client';
import { suspendKey } from '@/lib/history-key/session';
import { isFromEmbeddingPage } from '@/lib/utils/dom';

// Safety net for a logout no signal reported (a host button we do not know, an expired session).
const POLL_MS = 30_000;
// Chrome runs a hidden tab's timers about once a minute after five minutes, so a desktop tab
// whose poll kept running can go two polls between checks; longer means its timers stopped.
const SLEPT_WITHOUT_POLL_MS = 2 * POLL_MS + 15_000;
// One return to the page fires several of the events below (a back-forward cache restore fires
// `pageshow`, `resume` and `visibilitychange`), within moments of each other.
const WAKE_EVENTS_MS = 1_000;

/** Re-checks the session when something outside this tab says it may have changed: the host
 *  page around the chat, or an in-chat sign-in or sign-out in a sibling tab; and on a timer regardless. */
export function useSessionSignals(tenant: string | null): void {
  useEffect(() => {
    if (!tenant) return;
    const sessionTenant = tenant;
    // A frozen or cached page runs no timers, so the poll running is what proves the page was
    // awake, whatever its check answers. Not the check finishing: one sent just before a freeze
    // settles only once the page wakes.
    let aliveAt = Date.now();
    function check() {
      void checkSession(sessionTenant);
    }
    function poll() {
      // On waking, the overdue tick can run before `visibilitychange`: it must not vouch for the
      // sleep it is the first sign of.
      if (Date.now() - aliveAt > SLEPT_WITHOUT_POLL_MS) {
        afterSleep();
        return;
      }
      aliveAt = Date.now();
      check();
    }
    const timer = setInterval(poll, POLL_MS);
    function onMessage(event: MessageEvent) {
      if (!isFromEmbeddingPage(event)) return;
      if ((event.data as { type?: unknown } | null)?.type !== MESSAGE_TYPES.HOST_AUTH_CHANGED)
        return;
      check();
    }
    function onStorage(event: StorageEvent) {
      if (event.key === sessionChangeKey(sessionTenant) && event.newValue) check();
    }
    // Mobile browsers freeze background tabs and the back-forward cache suspends whole pages, so
    // neither the timer nor any signal ran meanwhile: history stays locked until a fresh check.
    const checkOnce = throttle(check, WAKE_EVENTS_MS, { trailing: false });
    let suspendedAt: number | undefined;
    function onReturn({ slept }: { slept: boolean }) {
      // Awake now, so a quick switch away and back before the next poll is no second sleep.
      aliveAt = Date.now();
      if (!slept) {
        checkOnce();
        return;
      }
      // The same return as the last suspension, whose check is already running.
      if (suspendedAt !== undefined && Date.now() - suspendedAt < WAKE_EVENTS_MS) return;
      suspendedAt = Date.now();
      suspendKey();
      // Never held back by an earlier check: only one that starts after the suspension lifts it.
      // Restarting the throttle runs it now and holds back the wake events that follow.
      checkOnce.cancel();
      checkOnce();
    }
    function afterSleep() {
      onReturn({ slept: true });
    }
    function onVisibility() {
      if (document.visibilityState === 'hidden') return;
      // A tab whose poll kept running while hidden keeps the key, however long it was away.
      onReturn({ slept: Date.now() - aliveAt > SLEPT_WITHOUT_POLL_MS });
    }
    function onPageShow(event: PageTransitionEvent) {
      if (event.persisted) afterSleep();
    }
    // Back online is no sign of sleep, only a good moment to ask again.
    function onOnline() {
      onReturn({ slept: false });
    }
    window.addEventListener('message', onMessage);
    window.addEventListener('storage', onStorage);
    window.addEventListener('pageshow', onPageShow);
    window.addEventListener('online', onOnline);
    document.addEventListener('visibilitychange', onVisibility);
    document.addEventListener('resume', afterSleep);
    return () => {
      clearInterval(timer);
      checkOnce.cancel();
      window.removeEventListener('message', onMessage);
      window.removeEventListener('storage', onStorage);
      window.removeEventListener('pageshow', onPageShow);
      window.removeEventListener('online', onOnline);
      document.removeEventListener('visibilitychange', onVisibility);
      document.removeEventListener('resume', afterSleep);
    };
  }, [tenant]);
}
